import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { getActiveBatch } from "../lib/api";
import { CRM } from "../config/content";

export default function BatchCountdown({ onBook }) {
  const [batch, setBatch] = useState(undefined);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    getActiveBatch().then(setBatch);
  }, []);

  useEffect(() => {
    if (!batch) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [batch]);

  if (!batch) return null;
  const diff = new Date(batch.start_date).getTime() - now;
  if (diff <= 0) return null;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const pct = batch.total_seats ? Math.round((batch.seats_left / batch.total_seats) * 100) : 0;

  const cells = [
    { v: days, l: "Days" },
    { v: hours, l: "Hours" },
    { v: mins, l: "Minutes" },
    { v: secs, l: "Seconds" },
  ];

  return (
    <section className="py-10 md:py-14 bg-[#0B110D] border-b border-[#17231B]" data-testid="batch-countdown-section">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-[#0F1712] border border-[#22C55E]/40 p-6 sm:p-10 shadow-[0_0_70px_-20px_rgba(34,197,94,0.4)] grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="inline-block w-8 h-[2px] bg-[#FACC15]" aria-hidden="true" />
              <span className="font-heading text-[#FACC15] text-sm tracking-[0.25em] uppercase">{CRM.batchSection.eyebrow}</span>
            </div>
            <h2 className="font-heading uppercase text-white text-3xl sm:text-4xl leading-none tracking-wide" data-testid="batch-countdown-title">
              {batch.name} <span className="text-[#22C55E]">STARTS IN</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-[#A7B3AA]">{batch.mode === "Both" ? "Online and offline at the workspace." : `${batch.mode} batch.`} Book your free check-up before the timer runs out.</p>
            <div className="mt-6">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-[#A7B3AA]">{batch.seats_left} of {batch.total_seats} {CRM.batchSection.seatsLabel}</span>
                <span className="font-bold text-[#FACC15]" data-testid="batch-seats-pct">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#17231B] overflow-hidden" data-testid="batch-seats-bar">
                <div className="h-full bg-[#22C55E] rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
          <div className="text-center lg:text-right">
            <div className="inline-grid grid-cols-4 gap-2 sm:gap-3" data-testid="batch-countdown-timer">
              {cells.map((c) => (
                <div key={c.l} className="rounded-2xl bg-[#070A08] border border-[#22C55E]/[0.2] px-3 sm:px-5 py-3 sm:py-4 min-w-[64px] sm:min-w-[84px]">
                  <p className="font-heading text-3xl sm:text-5xl text-white leading-none">{String(c.v).padStart(2, "0")}</p>
                  <p className="mt-1 text-[10px] sm:text-xs uppercase tracking-widest text-[#FACC15]">{c.l}</p>
                </div>
              ))}
            </div>
            <div className="mt-5">
              <button
                onClick={onBook}
                data-testid="batch-book-button"
                className="btn-primary group relative inline-flex items-center gap-2.5 rounded-full bg-[#22C55E] hover:bg-[#16A34A] text-[#04100A] font-extrabold text-sm sm:text-base px-7 py-3.5 transition-colors overflow-hidden"
              >
                <span className="relative z-10">Inquire Now</span>
                <ArrowRight className="w-4 h-4 relative z-10 arrow-slide" />
                <span className="shine" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
